import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AssetManager from "../../../common/AssetManager";
import CustomButton from "../../../common/components/CustomButton";
import useResolvedLang from "../../../common/hooks/useResolvedLang";
import { Context as SuggestionContext } from "../../../context/suggestionContext/SuggestionContext";
import {
  Clothes,
  ClothesLayer,
  Material,
  Palette,
} from "../../../context/suggestionContext/suggestionTypes";
import {
  AddToCartLink,
  SuggestionResult,
} from "../../../context/suggestionContext/types";
import LayersComponent from "./components/LayersComponent";
import SuggestedClothesCarouselComponent from "./components/SuggestedClothesCarouselComponent";
import "./suggestions.scss";

const Suggestions: React.FC<SuggestionsProps> = ({}) => {
  const { i18n } = useTranslation();

  const { state: suggestionState } = useContext(SuggestionContext);
  const { suggestions } = suggestionState;

  const [selectedLayer, setSelectedLayer] = useState<number>(0);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] =
    useState<number>(0);

  const [activeLayers, setActiveLayers] = useState<number[]>([]);
  const [activeAddToCartLink, setActiveAddToCartLink] =
    useState<AddToCartLink | null>(null);
  const lang = useResolvedLang();

  const clothes: Clothes[] =
    AssetManager.getAsset("/clothes_db.json")["clothes"];

  const materials: Material[] =
    AssetManager.getAsset("/materials_db.json")["materials"];

  const palettes: Palette[] = AssetManager.getAsset(
    "/clothing_palettes_db.json"
  )["palettes"];

  const [suggestedClothesForLayers, setSuggestedClothesForLayers] = useState<
    ClothesLayer[] | undefined
  >(undefined);

  const [clothesForSelectedLayer, setClothesForSelectedLayer] = useState<
    Clothes[]
  >([]);

  useEffect(() => {
    if (suggestions) {
      setActiveLayers(suggestions[selectedSuggestionIndex].layers.map(() => 0));
      setSelectedLayer(0);
      setActiveAddToCartLink(
        suggestions[selectedSuggestionIndex].addToCartLink
      );

      setSuggestedClothesForLayers(
        suggestions
          ? suggestions[selectedSuggestionIndex]?.layers.map((layer) => {
              return {
                clothes: layer?.map((id) => {
                  return clothes.find((cloth) => {
                    return +cloth.garmentIdSmall === id;
                  })!;
                }),
              };
            })
          : undefined
      );
    }
  }, [selectedSuggestionIndex]);

  useEffect(() => {
    if (suggestions) {
      setSelectedSuggestionIndex(0);
      setActiveLayers(suggestions[0].layers.map(() => 0));
      setSelectedLayer(0);
      setActiveAddToCartLink(suggestions[0].addToCartLink);

      setSuggestedClothesForLayers(
        suggestions
          ? suggestions[0]?.layers.map((layer) => {
              return {
                clothes: layer?.map((id) => {
                  return clothes.find((cloth) => {
                    return +cloth.garmentIdSmall === id;
                  })!;
                }),
              };
            })
          : undefined
      );
    }
  }, [suggestions]);

  useEffect(() => {
    setClothesForSelectedLayer(
      suggestedClothesForLayers
        ? suggestedClothesForLayers[selectedLayer].clothes
        : []
    );
  }, [suggestedClothesForLayers, selectedLayer]);

  function changeActiveLayer(layerIndex: number, activeIndex: number) {
    setActiveLayers((prev) => {
      const newValue = [...prev];
      newValue[layerIndex] = activeIndex;
      return newValue;
    });
  }

  return (
    <>
      <div className="suggestions-sections-container">
        <div className="suggestion-result-container">
          {renderSuggestionSection()}
        </div>
      </div>
    </>
  );

  function renderSuggestionSection() {
    switch (suggestionState.result) {
      case SuggestionResult.IN_PROGRESS:
        return renderLoading();
      case SuggestionResult.FOUND:
        return renderFoundSuggestions();
      case SuggestionResult.EXCEPTION_FOUND:
        return renderExceptionFound();
      case SuggestionResult.NOT_FOUND:
        return renderCannotSuggest();
      default:
        return <></>;
    }
  }

  function renderFoundSuggestions() {
    return (
      <div className="suggestion-found-container">
        {suggestions!.length >= 1 && (
          <div className="suggestions-sections-container">
            <p className="suggetion-section-label">
              {i18n.t("form_select_combination")}
            </p>

            <div className="combination-button-scroll-container">
              <div className="combination-button-container">
                {suggestions!.map((_, index) => {
                  return (
                    <div
                      key={index}
                      className={`combination-button ${
                        selectedSuggestionIndex === index
                          ? "combination-button-selected"
                          : ""
                      }`}
                      onClick={() => {
                        if (suggestions) {
                          setActiveLayers(
                            suggestions[index].layers.map(() => 0)
                          );
                        }
                        setSelectedSuggestionIndex(index);
                      }}
                    >
                      {i18n.t("suggestion_combination_tab_title", {
                        index: index + 1,
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="combination-layers-container">
              <p className="suggetion-section-label">
                {i18n.t("form_select_layer")}
              </p>

              <div className="combination-layers-inner-container">
                {activeLayers && activeLayers.length > 0 && (
                  <>
                    <div className="combination-layers-selector-container">
                      <LayersComponent
                        selectedLayer={selectedLayer}
                        clothesForLayers={suggestedClothesForLayers!}
                        onLayerSelectionChanged={(index) => {
                          setSelectedLayer(index);
                        }}
                        activeLayers={activeLayers}
                      />
                      {activeAddToCartLink && activeAddToCartLink[lang] && (
                        <CustomButton
                          label={i18n.t("combination_buy_all")}
                          icon="/cart.svg"
                          onClick={() => {
                            window.open(activeAddToCartLink[lang]!, "_blank");
                          }}
                          containerClass="add-to-cart-button"
                        ></CustomButton>
                      )}
                    </div>
                    <SuggestedClothesCarouselComponent
                      clothesForLayer={clothesForSelectedLayer}
                      materials={materials}
                      palettes={palettes}
                      changeActiveLayer={changeActiveLayer}
                      activeLayerIndex={selectedLayer}
                      activeLayers={activeLayers}
                      activeCombination={
                        suggestions
                          ? suggestions[selectedSuggestionIndex]
                          : null
                      }
                    ></SuggestedClothesCarouselComponent>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderLoading() {
    return <div>loading</div>;
  }

  function renderExceptionFound() {
    return (
      <div className="suggestions-exception-found">
        <h4>{i18n.t(suggestionState.exception!.exceptionResponseKey)}</h4>
      </div>
    );
  }

  function renderCannotSuggest() {
    return (
      <div className="suggestions-not-found">
        <h4>{i18n.t("suggestion_cannot_suggest_title")}</h4>
      </div>
    );
  }
};

export default Suggestions;

export interface SuggestionsProps {}
